import React, { useState, useEffect } from 'react';
import ChartBarIcon from './icons/ChartBarIcon';
import CartIcon from './icons/CartIcon';
import CalendarIcon from './icons/CalendarIcon';
import SlidersIcon from './icons/SlidersIcon';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';

interface PlanWalkthroughModalProps {
    isOpen: boolean;
    onClose: () => void;
    language?: Language;
}

const PlanWalkthroughModal: React.FC<PlanWalkthroughModalProps> = ({ isOpen, onClose, language }) => {
    const [step, setStep] = useState(1);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const [containerSize, setContainerSize] = useState({ width: 360, height: 600 });
    const t = (key: any) => getTranslation(language, key);

    useEffect(() => {
        if (isOpen) {
            setStep(1);
        }
    }, [isOpen]);

    const steps = [
        {
            id: 1,
            targetId: 'planner-view-toggle',
            placement: 'bottom',
            title: t('tutorial_plan_step1_title'),
            desc: t('tutorial_plan_step1_desc'),
            icon: <SlidersIcon className="w-8 h-8 text-sky-500" />
        },
        {
            id: 2,
            targetId: 'planner-stats-button',
            placement: 'bottom',
            title: t('tutorial_plan_step2_title'),
            desc: t('tutorial_plan_step2_desc'),
            icon: <ChartBarIcon className="w-8 h-8 text-indigo-500" />
        },
        {
            id: 3,
            targetId: 'planner-sync-button',
            placement: 'bottom',
            title: t('tutorial_plan_step3_title'),
            desc: t('tutorial_plan_step3_desc'),
            icon: <CartIcon className="w-8 h-8 text-emerald-500" />
        },
        {
            id: 4,
            targetId: 'planner-date-nav',
            placement: 'bottom',
            title: t('tutorial_plan_step4_title'),
            desc: t('tutorial_plan_step4_desc'),
            icon: <CalendarIcon className="w-8 h-8 text-amber-500" />
        },
        {
            id: 5,
            targetId: 'planner-today-button',
            placement: 'bottom',
            title: t('tutorial_plan_step5_title'),
            desc: t('tutorial_plan_step5_desc'),
            icon: <CalendarIcon className="w-8 h-8 text-rose-500" />
        },
        {
            id: 6,
            targetId: 'planner-add-btn',
            placement: 'bottom',
            title: t('tutorial_plan_step6_title'),
            desc: t('tutorial_plan_step6_desc'),
            icon: <CalendarIcon className="w-8 h-8 text-sky-500" />
        },
        {
            id: 7,
            targetId: 'planner-cook-btn',
            placement: 'bottom',
            title: t('tutorial_plan_step7_title'),
            desc: t('tutorial_plan_step7_desc'),
            icon: <ChartBarIcon className="w-8 h-8 text-emerald-500" />
        }
    ];

    const currentStep = steps.find(s => s.id === step)!;

    useEffect(() => {
        if (!isOpen) return;

        const getTargetEl = () => {
            let targetEl = document.getElementById(currentStep.targetId);
            if (!targetEl) {
                if (currentStep.targetId === 'planner-add-btn') {
                    targetEl = document.querySelector('[id^="planner-add-btn"]');
                } else if (currentStep.targetId === 'planner-cook-btn') {
                    targetEl = document.querySelector('[id^="planner-cook-btn"]');
                }
            }
            return targetEl;
        };

        const updateCoords = () => {
            const targetEl = getTargetEl();
            const containerEl = document.getElementById('app-container') || document.body;
            const containerRect = containerEl.getBoundingClientRect();
            
            setContainerSize({
                width: containerRect.width,
                height: containerRect.height
            });

            if (targetEl) {
                const targetRect = targetEl.getBoundingClientRect();
                setCoords({
                    top: targetRect.top - containerRect.top,
                    left: targetRect.left - containerRect.left,
                    width: targetRect.width,
                    height: targetRect.height
                });
            } else {
                setCoords({ top: 0, left: 0, width: 0, height: 0 });
            }
        };

        // If the element doesn't exist (like today button when on current period), skip it
        const targetEl = getTargetEl();
        if (!targetEl && isOpen) {
            if (currentStep.targetId === 'planner-today-button') {
                // Today button is not visible when on current week/month, just complete or skip to next
                if (step < steps.length) {
                    setStep(prev => prev + 1);
                } else {
                    onClose();
                }
                return;
            }
        }

        const timer = setTimeout(updateCoords, 100);
        window.addEventListener('resize', updateCoords);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateCoords);
        };
    }, [step, isOpen, currentStep.targetId]);

    if (!isOpen) return null;

    const isTargetInBottomHalf = coords.top > (containerSize.height / 2);

    const tooltipStyle: React.CSSProperties = isTargetInBottomHalf
        ? { top: '110px' }
        : { bottom: '110px' };

    let arrowPath = '';
    if (coords.width > 0) {
        const x1 = containerSize.width / 2;
        // Tooltip approximate height is ~180px
        const y1 = isTargetInBottomHalf 
            ? 110 + 175 // Bottom of top tooltip
            : containerSize.height - 110 - 175; // Top of bottom tooltip
            
        const x2 = coords.left + coords.width / 2;
        const y2 = isTargetInBottomHalf 
            ? coords.top - 8 // Point to top of target
            : coords.top + coords.height + 8; // Point to bottom of target

        const controlX = (x1 + x2) / 2;
        const controlY = isTargetInBottomHalf
            ? (y1 + y2) / 2 + 15
            : (y1 + y2) / 2 - 15;

        arrowPath = `M ${x1} ${y1} Q ${controlX} ${controlY} ${x2} ${y2}`;
    }

    return (
        <div className="absolute inset-0 z-[140] overflow-hidden animate-fade-in" onClick={onClose}>
            {/* SVG Mask and Curved Arrow */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                <defs>
                    <mask id="planner-spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {coords.width > 0 && (
                            <rect
                                x={coords.left - 6}
                                y={coords.top - 6}
                                width={coords.width + 12}
                                height={coords.height + 12}
                                rx="12"
                                fill="black"
                            />
                        )}
                    </mask>
                    <marker
                        id="planner-arrowhead"
                        markerWidth="10"
                        markerHeight="7"
                        refX="10"
                        refY="3.5"
                        orient="auto"
                    >
                        <polygon points="0 0, 10 3.5, 0 7" fill="#f97316" />
                    </marker>
                </defs>
                
                {/* Spotlight backdrop with blur */}
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(15, 23, 42, 0.65)"
                    mask="url(#planner-spotlight-mask)"
                    className="backdrop-blur-[3px] pointer-events-auto"
                    onClick={onClose}
                />
                
                {/* Animated Curved Arrow */}
                {coords.width > 0 && (
                    <path
                        d={arrowPath}
                        fill="none"
                        stroke="#f97316"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeDasharray="4 4"
                        markerEnd="url(#planner-arrowhead)"
                        className="animate-pulse"
                    />
                )}
            </svg>

            {/* Tour Tooltip Card */}
            <div 
                style={tooltipStyle}
                className="absolute left-4 right-4 w-[calc(100%-2rem)] max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-2xl z-20 p-5 border border-slate-100 dark:border-slate-800/80 flex flex-col gap-4"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center gap-3">
                    <div className="bg-sky-50 dark:bg-sky-900/20 p-2.5 rounded-2xl shadow-sm">
                        {currentStep.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-black text-brand-orange uppercase tracking-wider block mb-0.5">
                            {t('tutorial_step')} {step} / {steps.length}
                        </span>
                        <h3 className="text-base font-black text-slate-800 dark:text-slate-100 truncate">
                            {currentStep.title}
                        </h3>
                    </div>
                </div>

                <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-xs">
                    {currentStep.desc}
                </p>

                <div className="flex justify-between items-center mt-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button 
                        onClick={() => setStep(prev => Math.max(1, prev - 1))}
                        disabled={step === 1}
                        className="text-xs text-slate-400 dark:text-slate-500 font-bold hover:text-slate-600 dark:hover:text-slate-400 disabled:opacity-0 transition-opacity"
                    >
                        {t('previous')}
                    </button>
                    
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        >
                            {t('skip')}
                        </button>
                        {step < steps.length ? (
                            <button 
                                onClick={() => setStep(prev => prev + 1)}
                                className="px-5 py-2 bg-brand-orange text-white text-xs font-black rounded-lg hover:bg-orange-600 shadow-md shadow-orange-100 dark:shadow-none transition-all"
                            >
                                {t('nextStep')}
                            </button>
                        ) : (
                            <button 
                                onClick={onClose}
                                className="px-5 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-black rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 shadow-md transition-all"
                            >
                                {t('gotIt')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanWalkthroughModal;
